<template>
    <div class="period-card">
        <div class="period-card__head">
            <span class="period-card__subtitle">Σκορ ανά περίοδο</span>
            <span v-if="live" class="period-card__live"><i />LIVE</span>
        </div>

        <div class="period-scroll">
            <div class="period-grid" :style="{ '--periods': columns.length }">
                <!-- header -->
                <div class="pg-team pg-head" />
                <div
                    v-for="column in columns"
                    :key="`head-${column.period}`"
                    class="pg-head"
                    :class="{ 'pg-head--current': column.current }"
                >
                    {{ column.label }}
                    <i v-if="column.current" class="pg-live-dot" />
                </div>
                <div class="pg-head pg-final">Τελικό</div>

                <!-- one row per team -->
                <template v-for="side in sides">
                    <router-link :key="`team-${side.key}`" :to="teamRoute(side.team)" class="pg-team">
                        <span class="pg-team__logo">
                            <img v-if="side.team.logo" :src="`${assetsUrl}/${side.team.logo}`" alt="">
                            <v-icon v-else small color="orange">mdi-basketball</v-icon>
                        </span>
                        <span class="pg-team__name">{{ side.team.name }}</span>
                    </router-link>
                    <div
                        v-for="column in columns"
                        :key="`${side.key}-${column.period}`"
                        class="pg-cell"
                    >
                        <span
                            class="pg-score"
                            :class="{
                                'pg-score--won': column.played && column.winner === side.key,
                                'pg-score--lost': column.played && column.winner && column.winner !== side.key,
                                'pg-score--pending': !column.played
                            }"
                        >
                            {{ column.played ? column[side.key] : '–' }}
                        </span>
                    </div>
                    <div :key="`final-${side.key}`" class="pg-cell pg-final">
                        <span class="pg-total" :class="{ 'pg-total--won': finalWinner === side.key, 'pg-total--lost': finalWinner && finalWinner !== side.key }">
                            {{ totals[side.key] }}
                        </span>
                    </div>
                </template>

                <!-- who won each period, and by how much -->
                <div class="pg-team pg-diff-label">Διαφορά</div>
                <div v-for="column in columns" :key="`diff-${column.period}`" class="pg-cell">
                    <span
                        v-if="column.played && column.winner"
                        class="pg-diff"
                    >
                        +{{ Math.abs(column.team1 - column.team2) }}
                        <img v-if="logo(column.winner)" :src="logo(column.winner)" alt="">
                    </span>
                    <span v-else-if="column.played" class="pg-diff pg-diff--tie">=</span>
                </div>
                <div class="pg-cell pg-final">
                    <span
                        v-if="finalWinner"
                        class="pg-diff"
                    >
                        +{{ Math.abs(totals.team1 - totals.team2) }}
                        <img v-if="logo(finalWinner)" :src="logo(finalWinner)" alt="">
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    const ASSETS_URL = 'https://storage.googleapis.com'

    // Points of every period for the match page; the broadcast keeps its own PeriodScore
    export default {
        name: 'MatchPeriodScore',
        props: {
            team1: {
                type: Object,
                default: () => ({})
            },
            team2: {
                type: Object,
                default: () => ({})
            },
            periodScoresTeam1: {
                type: Object,
                default: () => ({})
            },
            periodScoresTeam2: {
                type: Object,
                default: () => ({})
            },
            // final score of the match, when known
            score1: {
                type: Number,
                default: null
            },
            score2: {
                type: Number,
                default: null
            },
            live: {
                type: Boolean,
                default: false
            },
            // the period being played, while live
            currentPeriod: {
                type: Number,
                default: null
            }
        },
        data() {
            return {
                assetsUrl: ASSETS_URL
            }
        },
        computed: {
            sides() {
                return [
                    { key: 'team1', team: this.team1 || {} },
                    { key: 'team2', team: this.team2 || {} }
                ]
            },
            // periods with points from either team
            scoredPeriods() {
                const keys = [...Object.keys(this.periodScoresTeam1 || {}), ...Object.keys(this.periodScoresTeam2 || {})]
                    .filter(key => Number.isFinite(Number(this.periodScoresTeam1[key])) || Number.isFinite(Number(this.periodScoresTeam2[key])))
                    .map(Number)
                    .filter(period => Number.isFinite(period) && period > 0)
                return Array.from(new Set(keys)).sort((a, b) => a - b)
            },
            lastPlayed() {
                const lastScored = this.scoredPeriods.length ? this.scoredPeriods[this.scoredPeriods.length - 1] : 0
                return Math.max(lastScored, this.live ? Number(this.currentPeriod) || 0 : 0)
            },
            columns() {
                const count = Math.max(4, this.lastPlayed)
                return Array.from({ length: count }, (_, index) => {
                    const period = index + 1
                    const played = period <= this.lastPlayed
                    const team1 = Number(this.periodScoresTeam1[period]) || 0
                    const team2 = Number(this.periodScoresTeam2[period]) || 0
                    let winner = null
                    if (team1 > team2) winner = 'team1'
                    if (team2 > team1) winner = 'team2'
                    return {
                        period,
                        label: period <= 4 ? `${period}η` : `Παρ. ${period - 4}`,
                        played,
                        current: this.live && period === this.lastPlayed,
                        team1,
                        team2,
                        winner
                    }
                })
            },
            totals() {
                const sum = key => this.columns.reduce((total, column) => total + (column.played ? column[key] : 0), 0)
                return {
                    team1: Number.isFinite(this.score1) && this.score1 > 0 ? this.score1 : sum('team1'),
                    team2: Number.isFinite(this.score2) && this.score2 > 0 ? this.score2 : sum('team2')
                }
            },
            finalWinner() {
                if (this.totals.team1 > this.totals.team2) return 'team1'
                if (this.totals.team2 > this.totals.team1) return 'team2'
                return null
            }
        },
        methods: {
            logo(key) {
                const team = key === 'team1' ? this.team1 : this.team2
                return team && team.logo ? `${ASSETS_URL}/${team.logo}` : null
            },
            teamRoute(team) {
                return team && team.id ? { name: 'Team', params: { id: team.id } } : {}
            }
        }
    }
</script>

<style scoped>
.period-card {
    padding: 16px 18px 12px;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    font-family: Ubuntu, Arial, sans-serif;
}

.period-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
}

.period-card__subtitle {
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.period-card__live {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    background: #ef4444;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.06em;
}

.period-card__live i,
.pg-live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
    animation: pg-pulse 1.4s ease-in-out infinite;
}

.pg-live-dot {
    display: inline-block;
    margin-left: 4px;
    color: #ef4444;
    vertical-align: middle;
}

@keyframes pg-pulse {
    0%,
    100% { opacity: 1; }
    50% { opacity: 0.3; }
}

.period-scroll {
    overflow-x: auto;
}

.period-grid {
    display: grid;
    grid-template-columns: minmax(170px, 1.6fr) repeat(var(--periods), minmax(58px, 1fr)) minmax(80px, 1fr);
    align-items: center;
}

.period-grid > * {
    padding: 8px 6px;
}

.pg-head {
    align-self: stretch;
    color: rgba(0, 0, 0, 0.5);
    font-size: 0.78rem;
    font-weight: 800;
    text-align: center;
}

.pg-head--current {
    color: #ef4444;
}

/* the team column stays visible when overtimes make the card scroll */
.pg-team {
    position: sticky;
    left: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    background: #fff;
    color: #2b2b2e !important;
    text-decoration: none;
}

.pg-team__logo {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    padding: 3px;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14);
}

.pg-team__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.pg-team__name {
    overflow: hidden;
    font-family: Mansalva, Arial, sans-serif;
    font-size: 1.05rem;
    font-weight: 800;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pg-team:hover .pg-team__name {
    color: #ef4444;
}

.pg-cell {
    display: flex;
    justify-content: center;
}

.pg-score {
    min-width: 40px;
    padding: 4px 8px;
    border-radius: 8px;
    color: #2b2b2e;
    font-size: 1.15rem;
    font-weight: 700;
    text-align: center;
    font-variant-numeric: tabular-nums;
}

/* same language as the team comparison: the better value darker and bigger */
.pg-score--won {
    color: #111;
    font-size: 1.3rem;
    font-weight: 800;
}

.pg-score--lost {
    color: rgba(0, 0, 0, 0.45);
}

.pg-score--pending {
    color: rgba(0, 0, 0, 0.25);
}

.pg-final {
    border-left: 1px solid rgba(0, 0, 0, 0.08);
}

.pg-head.pg-final {
    color: #2b2b2e;
}

.pg-total {
    font-size: 1.5rem;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
}

.pg-total--won {
    background-image: linear-gradient(90deg, #ef4444 0%, #ff7133 70%, orange 100%);
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

.pg-total--lost {
    color: rgba(0, 0, 0, 0.45);
}

.pg-diff-label {
    color: rgba(0, 0, 0, 0.45) !important;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.pg-diff {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 999px;
    background: #f4f4f6;
    color: #2b2b2e;
    font-size: 0.78rem;
    font-weight: 800;
}

.pg-diff img {
    width: 16px;
    height: 16px;
    object-fit: contain;
}

.pg-diff--tie {
    color: rgba(0, 0, 0, 0.45);
}

@media (max-width: 600px) {
    .period-card {
        padding: 14px 10px 8px;
    }

    .period-grid {
        /* numbers take only what they need, the team names get the rest */
        grid-template-columns: 46px repeat(var(--periods), minmax(38px, 1fr)) minmax(50px, 1fr);
    }

    .period-grid > * {
        padding: 7px 3px;
    }

    .pg-team {
        gap: 6px;
    }

    .pg-team__logo {
        width: 30px;
        height: 30px;
    }

    /* logos only: the names are in the match header right above */
    .pg-team__name {
        display: none;
    }

    .pg-diff-label {
        font-size: 0.6rem;
        letter-spacing: 0;
    }

    .pg-score--won {
        font-size: 1.1rem;
    }

    .pg-score {
        min-width: 30px;
        padding: 3px 4px;
        font-size: 1rem;
    }

    .pg-total {
        font-size: 1.2rem;
    }

    .pg-diff {
        gap: 2px;
        padding: 2px 4px;
        font-size: 0.68rem;
    }

    .pg-diff img {
        width: 12px;
        height: 12px;
    }
}
</style>
